import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, User, MapPin, ArrowLeft, Plus, Edit, Eye, Trash2 } from 'lucide-react';
import { useTaskTemplates } from '../../hooks/useTaskTemplates';
import { useTasks, useTaskMutations } from '../../hooks/useTasks';
import { useUserGroups } from '../../hooks/useUserGroups';
import { useTaskTypes } from '../../hooks/useTaskNomenclatures';
import { useProtocols } from '../../hooks/useProtocols';
import TaskExecutionView from './TaskExecutionView';
// rbacService removed - using userService instead
import { taskService } from '../../services/taskService';
import { riskService } from '../../services/riskService';
import { riskCalculationService } from '../../services/riskCalculationService';
import {
  organizationService,
  locationService,
  buildingService,
  floorService,
  departmentService,
  roomService,
  bedService,
  equipmentService,
  personnelService
} from '../../services/firebaseService';
import { userService } from '../../services/firestoreService';
// Remove the clinical context import - we'll use direct service calls
import './TaskGenerator.css';

const TaskGenerator = ({ userId }) => {
  const { t } = useTranslation('tasks');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [generationMode, setGenerationMode] = useState('template'); // 'template' or 'protocol'
  const [step, setStep] = useState(1); // 1: Select Template/Protocol, 2: Configure Context, 3: Assign & Schedule
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [customContext, setCustomContext] = useState({});
  const [assignedTo, setAssignedTo] = useState('');
  const [assignedToName, setAssignedToName] = useState('');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [generatedTask, setGeneratedTask] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    taskType: '',
    status: ''
  });
  const [templateFilters, setTemplateFilters] = useState({
    searchTerm: '',
    taskType: '',
    isActive: true
  });

  // Load data
  const { tasks, loading: tasksLoading, error: loadError, refresh } = useTasks(filters);
  const { templates, loading: templatesLoading } = useTaskTemplates(templateFilters);
  const { groups } = useUserGroups({ isActive: true });
  const { protocols, loading: protocolsLoading } = useProtocols({ status: 'active' });
  const { createFromTemplate, deleteTask } = useTaskMutations();
  const { items: taskTypes } = useTaskTypes();

  // Helper function to get task type name
  const getTaskTypeName = (taskTypeId) => {
    if (!taskTypeId || !taskTypes) return 'Unknown';
    const taskType = taskTypes.find(type => type.id === taskTypeId);
    return taskType ? taskType.name : 'Unknown';
  };

  // Clinical structure data
  const [clinicalData, setClinicalData] = useState({
    organizations: [],
    locations: [],
    buildings: [],
    floors: [],
    departments: [],
    rooms: [],
    beds: [],
    equipment: [],
    personnel: [],
    loading: true
  });

  // Users data - will be loaded when needed
  const [users, setUsers] = useState([]);

  // Load clinical structure data
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('🔍 TaskGenerator: Loading clinical data...');
        
        const [orgs, locs, bldgs, flrs, depts, rms, bds, equip, pers, usersData] = await Promise.all([
          organizationService.getAll(),
          locationService.getAll(),
          buildingService.getAll(),
          floorService.getAll(),
          departmentService.getAll(),
          roomService.getAll(),
          bedService.getAll(),
          equipmentService.getAll(),
          personnelService.getAll(),
          userService.getAll()
        ]);

        console.log('🔍 TaskGenerator: Loaded clinical data:', {
          organizations: orgs?.length || 0,
          locations: locs?.length || 0,
          buildings: bldgs?.length || 0,
          floors: flrs?.length || 0,
          departments: depts?.length || 0,
          rooms: rms?.length || 0,
          beds: bds?.length || 0,
          equipment: equip?.length || 0,
          personnel: pers?.length || 0,
          users: usersData?.length || 0
        });

        setClinicalData({
          organizations: orgs || [],
          locations: locs || [],
          buildings: bldgs || [],
          floors: flrs || [],
          departments: depts || [],
          rooms: rms || [],
          beds: bds || [],
          equipment: equip || [],
          personnel: pers || [],
          loading: false
        });

        setUsers(usersData || []);
      } catch (error) {
        console.error('🔍 TaskGenerator: Error loading clinical data:', error);
        setClinicalData(prev => ({ ...prev, loading: false }));
      }
    };

    loadData();
  }, []);

  const handleCancelGeneration = () => {
    setShowTemplateSelector(false);
    setStep(1);
    setGenerationMode('template');
    setSelectedTemplate(null);
    setSelectedProtocol(null);
    setCustomContext({});
    setAssignedTo('');
    setAssignedToName('');
    setScheduledStart('');
    setScheduledTime('');
    setError(null);
  };

  const handleContextChange = (field, value, nameField = null, nameValue = null) => {
    setCustomContext(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      if (nameField && nameValue) {
        updated[nameField] = nameValue;
      }
      
      return updated;
    });
  };

  const handleGenerateTask = async () => {
    try {
      setGenerating(true);
      setError(null);

      // Combine date and time
      const startDateTime = new Date(`${scheduledStart}T${scheduledTime}`);

      if (generationMode === 'protocol' && selectedProtocol) {
        // Generate tasks from protocol
        console.log('🔍 TaskGenerator: Generating tasks from protocol:', {
          protocolId: selectedProtocol.id,
          protocolName: selectedProtocol.name,
          customContext,
          startDateTime,
          userId
        });
        
        const taskResults = await taskService.generateTasksFromProtocol(
          selectedProtocol.id,
          customContext,
          startDateTime,
          userId
        );
        
        console.log(`✅ Generated ${taskResults.length} tasks from protocol`);
      } else if (generationMode === 'template' && selectedTemplate) {
        // Create task from template
        console.log('🔍 TaskGenerator: Creating task with:', {
          templateId: selectedTemplate?.id,
          templateName: selectedTemplate?.name,
          selectedTemplateFull: selectedTemplate,
          customContext,
          assignedTo,
          startDateTime,
          userId
        });
        
        const taskId = await createFromTemplate(
          selectedTemplate.id,
          customContext,
          assignedTo,
          assignedToName,
          startDateTime,
          userId
        );
      } else {
        throw new Error('Please select a template or protocol');
      }

      // Reset form and close modal
      setShowTemplateSelector(false);
      setStep(1);
      setGenerationMode('template');
      setSelectedTemplate(null);
      setSelectedProtocol(null);
      setCustomContext({});
      setAssignedTo('');
      setAssignedToName('');
      setScheduledStart('');
      setScheduledTime('');
      
      // Refresh task list
      refresh();
    } catch (error) {
      console.error('Error generating task:', error);
      setError(error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleQuickTaskGeneration = () => {
    // Switch to template/protocol selection mode
    setStep(1);
    setGenerationMode('template');
    setSelectedTemplate(null);
    setSelectedProtocol(null);
    setShowTemplateSelector(true);
  };

  const handleTemplateSelect = (template) => {
    console.log('🔍 TaskGenerator: Template selected:', {
      id: template.id,
      name: template.name,
      fullTemplate: template
    });
    setSelectedTemplate(template);
    setSelectedProtocol(null);
    setGenerationMode('template');
    setCustomContext(template.defaultContext || {});
    setShowTemplateSelector(true);
    setStep(2);
  };

  const handleProtocolSelect = (protocol) => {
    console.log('🔍 TaskGenerator: Protocol selected:', {
      id: protocol.id,
      name: protocol.name,
      fullProtocol: protocol
    });
    setSelectedProtocol(protocol);
    setSelectedTemplate(null);
    setGenerationMode('protocol');
    setCustomContext({});
    setShowTemplateSelector(true);
    setStep(2);
  };

  const [viewingTask, setViewingTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);

  const handleViewTask = async (taskId) => {
    try {
      const task = await taskService.getTask(taskId);
      setViewingTask(task);
      setEditingTask(null);
    } catch (error) {
      console.error('Error loading task:', error);
      alert(`Failed to load task: ${error.message}`);
    }
  };

  const handleEditTask = async (taskId) => {
    try {
      const task = await taskService.getTask(taskId);
      setEditingTask(task);
      setViewingTask(null);
    } catch (error) {
      console.error('Error loading task:', error);
      alert(`Failed to load task: ${error.message}`);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await deleteTask(taskId);
        refresh();
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  // Task View/Edit Modal Component (shared across all views)
  const TaskModals = () => (
    <>
      {viewingTask && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setViewingTask(null)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '90%',
              maxHeight: '90%',
              width: '1000px',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <TaskExecutionView 
              task={viewingTask} 
              userId={userId}
              onBack={() => {
                setViewingTask(null);
                refresh(); // Refresh task list
              }}
            />
          </div>
        </div>
      )}

      {editingTask && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setEditingTask(null)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '90%',
              maxHeight: '90%',
              width: '1000px',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <TaskExecutionView 
              task={editingTask} 
              userId={userId}
              onBack={() => {
                setEditingTask(null);
                refresh(); // Refresh task list
              }}
            />
          </div>
        </div>
      )}
    </>
  );

  // If not in template selector mode, show task list
  if (!showTemplateSelector) {
    return (
      <>
        <div className="task-generator">
        <div className="generator-header">
          <div>
            <h2>{t('generatedTasksTitle')}</h2>
            <p className="subtitle">{t('generatedTasksSubtitle')}</p>
          </div>
          <button className="btn btn-primary" onClick={handleQuickTaskGeneration}>
            <Plus size={16} />
            {t('generateNewTask')}
          </button>
        </div>

        {/* Filters */}
        <div className="template-filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder={t('searchTasksPlaceholder')}
              value={filters.searchTerm}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={filters.taskType}
              onChange={(e) => setFilters(prev => ({ ...prev, taskType: e.target.value }))}
              className="filter-select"
            >
              <option value="">{t('allTaskTypes')}</option>
              {taskTypes && taskTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="filter-select"
            >
              <option value="">{t('allStatuses')}</option>
              <option value="pending">{t('pending')}</option>
              <option value="in_progress">{t('inProgress')}</option>
              <option value="completed">{t('completed')}</option>
              <option value="cancelled">{t('cancelled')}</option>
            </select>
          </div>
        </div>

        {tasksLoading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>{t('loadingTasks')}</p>
          </div>
        ) : loadError ? (
          <div className="error-state">
            <p>{t('errorLoadingTasks')} {loadError}</p>
            <button onClick={refresh} className="btn btn-secondary">{t('retry')}</button>
          </div>
        ) : tasks.length === 0 ? (
          <div className="empty-state">
            <p>{t('noGeneratedTasksFound')}</p>
            <p className="empty-hint">{t('generateFirstTaskHint')}</p>
          </div>
        ) : (
          <div className="template-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('taskName')}</th>
                  <th>{t('templateUsed')}</th>
                  <th>{t('assignedTo')}</th>
                  <th>{t('statusLabel')}</th>
                  <th>{t('dueDate')}</th>
                  <th>{t('priority')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => (
                  <tr key={task.id} className="template-row">
                    <td>
                      <div className="template-name-cell">
                        <strong>{task.name || task.templateName}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="task-type-badge">
                        {task.templateName || t('unknownTemplate')}
                      </span>
                    </td>
                    <td>
                      <span className="assigned-badge">
                        {task.assignedToName || task.assignedTo || t('unassigned')}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${task.status}`}>
                        {task.status === 'in_progress' ? t('inProgress') : task.status === 'completed' ? t('completed') : task.status === 'cancelled' ? t('cancelled') : t('pending')}
                      </span>
                    </td>
                    <td>
                      <span className="due-date-badge">
                        {task.scheduledEnd ? new Date(task.scheduledEnd).toLocaleDateString() : t('notSet')}
                      </span>
                    </td>
                    <td>
                      <span className={`priority-badge priority-${task.priority}`}>
                        {task.priority === 'high' ? t('priorityHigh') : task.priority === 'low' ? t('priorityLow') : t('priorityMedium')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTask(task.id);
                          }}
                          title={t('viewTask')}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="btn btn-edit btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTask(task.id);
                          }}
                          title={t('editTask')}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn btn-delete btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task.id);
                          }}
                          title={t('deleteTask')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
        <TaskModals />
      </>
    );
  }

  // Step 1: Template/Protocol Selection (when in generation mode)
  if (showTemplateSelector && step === 1) {
    // Filter protocols based on search
    const filteredProtocols = protocols?.filter(protocol => {
      if (!templateFilters.searchTerm) return true;
      const searchLower = templateFilters.searchTerm.toLowerCase();
      return (
        protocol.name?.toLowerCase().includes(searchLower) ||
        protocol.protocol_code?.toLowerCase().includes(searchLower) ||
        protocol.description?.toLowerCase().includes(searchLower)
      );
    }) || [];

    // Filter templates based on existing filters
    const filteredTemplates = templates?.filter(template => {
      if (templateFilters.searchTerm) {
        const searchLower = templateFilters.searchTerm.toLowerCase();
        if (!template.name?.toLowerCase().includes(searchLower) &&
            !template.description?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      if (templateFilters.taskType && template.taskType !== templateFilters.taskType) {
        return false;
      }
      if (templateFilters.isActive !== undefined && template.isActive !== templateFilters.isActive) {
        return false;
      }
      return true;
    }) || [];

    return (
      <>
        <div className="task-generator">
        <div className="generator-header">
          <div>
            <h2>Select Source</h2>
            <p className="subtitle">Choose a template or protocol to generate tasks</p>
          </div>
          <button className="btn btn-secondary" onClick={handleCancelGeneration}>
            <ArrowLeft size={16} />
            Back to Tasks
          </button>
        </div>

        {/* Mode Selector */}
        <div className="mode-selector" style={{ marginBottom: '20px', display: 'flex', gap: '12px', padding: '12px', background: '#f9fafb', borderRadius: '4px' }}>
          <button
            className={`btn ${generationMode === 'template' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setGenerationMode('template');
              setSelectedProtocol(null);
            }}
          >
            From Template
          </button>
          <button
            className={`btn ${generationMode === 'protocol' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setGenerationMode('protocol');
              setSelectedTemplate(null);
            }}
          >
            From Protocol
          </button>
        </div>

        {/* Template/Protocol Filters */}
        <div className="template-filters">
          <div className="filter-group">
            <input
              type="text"
              placeholder={generationMode === 'template' ? "Search templates..." : "Search protocols..."}
              value={templateFilters.searchTerm}
              onChange={(e) => setTemplateFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={templateFilters.taskType}
              onChange={(e) => setTemplateFilters(prev => ({ ...prev, taskType: e.target.value }))}
              className="filter-select"
            >
              <option value="">{t('allTaskTypes')}</option>
              {taskTypes && taskTypes.map(type => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={templateFilters.isActive ? 'active' : 'inactive'}
              onChange={(e) => setTemplateFilters(prev => ({ ...prev, isActive: e.target.value === 'active' }))}
              className="filter-select"
            >
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
              <option value="">All Templates</option>
            </select>
          </div>
        </div>

        {generationMode === 'template' ? (
          <>
            {templatesLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading templates...</p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="empty-state">
                <p>No task templates available</p>
                <p className="empty-hint">Create a task template first</p>
              </div>
            ) : (
              <div className="template-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Template Name</th>
                      <th>Description</th>
                      <th>Task Type</th>
                      <th>Duration</th>
                      <th>Priority</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTemplates.map(template => (
                      <tr key={template.id} className="template-row" onClick={() => handleTemplateSelect(template)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="template-name-cell">
                            <strong>{template.name}</strong>
                          </div>
                        </td>
                        <td>
                          <div className="template-description-cell" title={template.description}>
                            {template.description ? 
                              (template.description.length > 80 
                                ? template.description.substring(0, 80) + '...' 
                                : template.description) 
                              : 'No description'
                            }
                          </div>
                        </td>
                        <td>
                          <span className="task-type-badge">
                            {getTaskTypeName(template.taskType)}
                          </span>
                        </td>
                        <td>
                          <span className="duration-badge">
                            {template.timingRules?.estimatedDuration} {template.timingRules?.durationUnit}
                          </span>
                        </td>
                        <td>
                          <span className={`priority-badge priority-${template.defaultPriority}`}>
                            {template.defaultPriority}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTemplateSelect(template);
                            }}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            {protocolsLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading protocols...</p>
              </div>
            ) : filteredProtocols.length === 0 ? (
              <div className="empty-state">
                <p>No protocols available</p>
                <p className="empty-hint">Create a protocol first in Protocol Configuration</p>
              </div>
            ) : (
              <div className="template-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Protocol Name</th>
                      <th>Protocol Code</th>
                      <th>Description</th>
                      <th>Category</th>
                      <th>Version</th>
                      <th>Steps</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProtocols.map(protocol => (
                      <tr key={protocol.id} className="template-row" onClick={() => handleProtocolSelect(protocol)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="template-name-cell">
                            <strong>{protocol.name}</strong>
                          </div>
                        </td>
                        <td>
                          <span className="task-type-badge" style={{ fontFamily: 'monospace' }}>
                            {protocol.protocol_code || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <div className="template-description-cell" title={protocol.description}>
                            {protocol.description ? 
                              (protocol.description.length > 80 
                                ? protocol.description.substring(0, 80) + '...' 
                                : protocol.description) 
                              : 'No description'
                            }
                          </div>
                        </td>
                        <td>
                          <span className="task-type-badge">
                            {protocol.category || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span className="duration-badge">
                            {protocol.version || '1.0'}
                          </span>
                        </td>
                        <td>
                          <span className="priority-badge">
                            {(protocol.steps || []).length} steps
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProtocolSelect(protocol);
                            }}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        </div>
        <TaskModals />
      </>
    );
  }

  // Step 2: Context Configuration
  if (showTemplateSelector && step === 2) {
    return (
      <>
        <div className="task-generator">
        <div className="generator-header">
          <h2>Configure Task Context</h2>
          <p className="subtitle">
            {generationMode === 'template' && selectedTemplate 
              ? `Template: ${selectedTemplate.name}`
              : generationMode === 'protocol' && selectedProtocol
              ? `Protocol: ${selectedProtocol.name}`
              : 'Select a template or protocol'}
          </p>
        </div>

        {clinicalData.loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading clinical data...</p>
          </div>
        ) : clinicalData.departments?.length === 0 && clinicalData.rooms?.length === 0 && clinicalData.beds?.length === 0 ? (
          <div className="empty-state">
            <p>No clinical data available</p>
            <p className="empty-hint">Please ensure the hospital administration structure is properly configured</p>
            <button className="btn btn-secondary" onClick={() => window.location.hash = '#/clinical-mapper'}>
              Go to Clinical Mapper
            </button>
          </div>
        ) : (
          <div className="context-form">
            <div className="form-section">
              <h3><MapPin size={16} /> Location Context</h3>
              
              {/* Debug info - remove this later */}
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                Firebase Data - Departments: {clinicalData.departments?.length || 0}, 
                Rooms: {clinicalData.rooms?.length || 0}, 
                Beds: {clinicalData.beds?.length || 0}
              </div>
              
              <div className="form-grid">
              <div className="form-group">
                <label>Department</label>
                <select
                  value={customContext.departmentId || ''}
                  onChange={(e) => {
                    const dept = clinicalData.departments.find(d => d.id === e.target.value);
                    handleContextChange('departmentId', e.target.value, 'departmentName', dept?.name);
                  }}
                >
                  <option value="">Select Department</option>
                  {clinicalData.departments && clinicalData.departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Room</label>
                <select
                  value={customContext.roomId || ''}
                  onChange={(e) => {
                    const room = clinicalData.rooms.find(r => r.id === e.target.value);
                    handleContextChange('roomId', e.target.value, 'roomName', room?.name);
                  }}
                >
                  <option value="">Select Room</option>
                  {clinicalData.rooms && clinicalData.rooms
                    .filter(r => !customContext.departmentId || r.departmentId === customContext.departmentId)
                    .map(room => (
                      <option key={room.id} value={room.id}>{room.name}</option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Bed</label>
                <select
                  value={customContext.bedId || ''}
                  onChange={(e) => handleContextChange('bedId', e.target.value)}
                >
                  <option value="">Select Bed</option>
                  {clinicalData.beds && clinicalData.beds
                    .filter(b => !customContext.roomId || b.roomId === customContext.roomId)
                    .map(bed => (
                      <option key={bed.id} value={bed.id}>{bed.number}</option>
                    ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Additional Context</h3>
            
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea
                value={customContext.notes || ''}
                onChange={(e) => handleContextChange('notes', e.target.value)}
                placeholder="Additional context or special instructions..."
                rows={3}
              />
            </div>
          </div>

          <div className="step-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
              Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(3)}>
              Next: Assignment
            </button>
          </div>
          </div>
        )}
        </div>
        <TaskModals />
      </>
    );
  }

  // Step 3: Assignment & Scheduling
  if (showTemplateSelector && step === 3) {
    const isProtocolMode = generationMode === 'protocol' && selectedProtocol;
    const selectedItem = isProtocolMode ? selectedProtocol : selectedTemplate;
    
    return (
      <>
        <div className="task-generator">
        <div className="generator-header">
          <h2>Assignment & Scheduling</h2>
          <p className="subtitle">
            {isProtocolMode 
              ? `Protocol: ${selectedProtocol.name}`
              : `Template: ${selectedTemplate.name}`}
          </p>
        </div>

        <div className="assignment-form">
          {!isProtocolMode && (
            <div className="form-section">
              <h3><User size={16} /> Assignment</h3>
              
              <div className="form-grid">
                {selectedTemplate?.assignmentRules?.assignmentType === 'group_based' && selectedTemplate.assignmentRules.assignedToGroup ? (
                <div className="form-group full-width">
                  <label>Assigned to Group</label>
                  <input
                    type="text"
                    value={groups.find(g => g.id === selectedTemplate.assignmentRules.assignedToGroup)?.name || 'Group'}
                    disabled
                    className="readonly-input"
                  />
                  <p className="field-hint">Task will be auto-assigned to an available member</p>
                </div>
              ) : (
                <div className="form-group full-width">
                  <label>Assign to Personnel *</label>
                  <select
                    value={assignedTo}
                    onChange={(e) => {
                      const user = users.find(u => u.id === e.target.value);
                      setAssignedTo(e.target.value);
                      setAssignedToName(user?.name || user?.email || '');
                    }}
                    required
                  >
                    <option value="">Select Personnel</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email} - {user.role || 'User'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            </div>
          )}

          {isProtocolMode && (
            <div className="form-section" style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '4px' }}>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                <strong>Note:</strong> Tasks generated from this protocol will use assignment rules defined at the step and action level within the protocol.
                You only need to set the schedule below.
              </p>
            </div>
          )}

          <div className="form-section">
            <h3><Calendar size={16} /> Scheduling</h3>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  value={scheduledStart}
                  onChange={(e) => setScheduledStart(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label>Start Time *</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  required
                />
              </div>

              {!isProtocolMode && selectedTemplate?.timingRules && (
                <>
                  <div className="form-group">
                    <label><Clock size={14} /> Estimated Duration</label>
                    <input
                      type="number"
                      value={selectedTemplate.timingRules.estimatedDuration || ''}
                      onChange={(e) => {
                        setCustomContext(prev => ({
                          ...prev,
                          estimatedDuration: e.target.value
                        }));
                      }}
                      min="1"
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Duration Unit</label>
                    <select
                      value={selectedTemplate.timingRules.durationUnit || 'minutes'}
                      onChange={(e) => {
                        setCustomContext(prev => ({
                          ...prev,
                          durationUnit: e.target.value
                        }));
                      }}
                      className="form-select"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="step-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancelGeneration}>
              Cancel
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setStep(2)}>
              <ArrowLeft size={16} /> Back
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerateTask}
              disabled={generating || (!isProtocolMode && !assignedTo) || !scheduledStart || !scheduledTime}
            >
              {generating ? 'Generating...' : 'Generate Task'}
            </button>
            
            {/* Debug info - remove this later */}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              Debug: assignedTo={assignedTo ? 'set' : 'missing'}, 
              scheduledStart={scheduledStart ? 'set' : 'missing'}, 
              scheduledTime={scheduledTime ? 'set' : 'missing'}
            </div>
          </div>
        </div>
        </div>
        <TaskModals />
      </>
    );
  }

  return <TaskModals />;
};

export default TaskGenerator;

